import { useAuth } from '../../contexts/AuthContext';
import React, { useEffect, useState, useMemo } from 'react';
import { TrendingUp, Settings, CheckCircle, Clock, RefreshCw, Search, User, Building2, Calendar, Award, ArrowRight, Plus, Trash2, History, AlertCircle, FileText } from 'lucide-react';
import useEmployeeMasters from '../../hooks/useEmployeeMasters';
import { buildApiUrl } from '../../utils/apiSetup';
import SearchableSelect from '../common/SearchableSelect';
import { formatDate } from '../../utils/dateUtils';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

const PromotionIncrementManagement = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('increment');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // info, success, error
  
  // Dashboard stats
  const [stats, setStats] = useState({
    regularIncrementDue: 0,
    functionalPromotionDue: 0,
    promotionsProcessed: 0,
    totalHistory: 0
  });
  
  // Employee search
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [showEmployeeSuggestions, setShowEmployeeSuggestions] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeProgressionStatus, setEmployeeProgressionStatus] = useState(null);
  const [loadingEmployeeStatus, setLoadingEmployeeStatus] = useState(false);
  
  // Action type
  const [actionType, setActionType] = useState('regular_increment'); // regular_increment, promotion, functional_promotion
  
  // Regular Increment form
  const [regularIncrementForm, setRegularIncrementForm] = useState({
    increment_type: 'percentage',
    increment_percentage: 3,
    increment_amount: 0,
    applicable_date: new Date().toISOString().slice(0, 10),
    remarks: ''
  });
  
  // Promotion form
  const [promotionForm, setPromotionForm] = useState({
    to_designation_id: '',
    change_basic_pay: false,
    new_basic_salary: '',
    effective_date: new Date().toISOString().slice(0, 10),
    remarks: ''
  });
  
  // Functional Promotion form
  const [functionalPromotionForm, setFunctionalPromotionForm] = useState({
    milestone_years: '',
    effective_date: new Date().toISOString().slice(0, 10),
    remarks: ''
  });
  
  // Configuration - Promotion Mappings
  const [promotionMappings, setPromotionMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [newMapping, setNewMapping] = useState({
    department_id: '',
    from_designation_id: '',
    to_designation_id: ''
  });
  
  // Configuration - Functional Promotion Rules
  const [functionalConfigs, setFunctionalConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  
  // History
  const [combinedHistory, setCombinedHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Reference data - use useEmployeeMasters hook
  const { masters, loading: mastersLoading, fetchDesignations, fetchDepartmentHeads } = useEmployeeMasters();
  const departments = masters?.departments || [];
  const [filteredFromDesignations, setFilteredFromDesignations] = useState([]);
  const [filteredToDesignations, setFilteredToDesignations] = useState([]);
  const [allDepartmentDesignations, setAllDepartmentDesignations] = useState([]);
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);

  // --- PROMOTION DESTINATION STATES ---
  const [selectedDestinationDesigId, setSelectedDestinationDesigId] = useState('');
  const [destinationDepartmentId, setDestinationDepartmentId] = useState('');
  const [destinationHeadId, setDestinationHeadId] = useState('');
  const [departmentHeadsList, setDepartmentHeadsList] = useState([]);
  const [loadingHeads, setLoadingHeads] = useState(false);

  // Mapped options for SearchableSelect (unique valid destination designations)
  const promotionDestinationOptions = useMemo(() => {
    return (employeeProgressionStatus?.valid_promotion_destinations || []).map((dest) => ({
      value: String(dest.id),
      label: `${dest.title || dest.label || ''}${dest.designation_type ? ` (${dest.designation_type})` : ''}`.trim(),
      subLabel: dest.code ? `Code: ${dest.code}` : undefined,
      departments: dest.departments || [],
      raw: dest
    }));
  }, [employeeProgressionStatus?.valid_promotion_destinations]);

  const validProgressionDestinationOptions = promotionDestinationOptions;

  const selectedDestinationObj = useMemo(() => {
    if (!selectedDestinationDesigId) return null;
    return (employeeProgressionStatus?.valid_promotion_destinations || []).find(
      (d) => String(d.id) === String(selectedDestinationDesigId)
    ) || null;
  }, [selectedDestinationDesigId, employeeProgressionStatus?.valid_promotion_destinations]);

  const availableDepartmentsForSelectedDesig = useMemo(() => {
    if (!selectedDestinationObj) return [];
    return selectedDestinationObj.departments || [];
  }, [selectedDestinationObj]);

  // --- HISTORICAL PROMOTION STATES ---
  const [historicalRows, setHistoricalRows] = useState([]);
  const [savingHistorical, setSavingHistorical] = useState(false);
  const [deletingHistoricalId, setDeletingHistoricalId] = useState(null);

  // All active designations formatted for SearchableSelect in historical promotions
  const allDesignationOptions = useMemo(() => {
    return (masters?.designations || []).map((d) => ({
      value: String(d.value),
      label: d.label,
      subLabel: d.grade ? `Grade: ${d.grade}` : undefined,
    }));
  }, [masters?.designations]);

  const allDepartmentList = useMemo(() => {
    return (masters?.departments || []).map((dept) => ({
      value: String(dept.value),
      label: `${dept.label}${dept.code ? ` (${dept.code})` : ''}`,
    }));
  }, [masters?.departments]);

  // --- NEW INCREMENT TAB STATES ---
  const [incrementMonth, setIncrementMonth] = useState('');
  const [incrementYear, setIncrementYear] = useState('');
  const [eligibleEmployees, setEligibleEmployees] = useState([]);
  const [selectedEligibleIds, setSelectedEligibleIds] = useState(new Set());
  const [incrementSearch, setIncrementSearch] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [bulkIncrementPercentage, setBulkIncrementPercentage] = useState(3);
  const [loadingEligible, setLoadingEligible] = useState(false);

  // --- FUNCTIONAL PROMOTION BULK TAB STATES ---
  const [fpEligibleEmployees, setFpEligibleEmployees] = useState([]);
  const [selectedFpIds, setSelectedFpIds] = useState(new Set());
  const [fpSearch, setFpSearch] = useState('');
  const [showFpPreviewModal, setShowFpPreviewModal] = useState(false);
  const [fpEffectiveDate, setFpEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [loadingFpEligible, setLoadingFpEligible] = useState(false);
  const [fpBulkPercentage, setFpBulkPercentage] = useState(3);
  const [showViewSelectedFpModal, setShowViewSelectedFpModal] = useState(false);

  // --- NEW INCREMENT TAB METHODS ---
  const effectiveDateForIncrement = useMemo(() => {
    if (incrementMonth && incrementYear) {
      return `${incrementYear}-${incrementMonth}-01`;
    }
    return '';
  }, [incrementMonth, incrementYear]);

  const fetchEligibleEmployees = async (isReload = false) => {
    if (!incrementMonth || !incrementYear) return;
    setLoadingEligible(true);
    try {
      const ym = `${incrementYear}-${incrementMonth}`;
      const response = await fetch(buildApiUrl(`/api/v1/increments/eligibility?eligible_month=${ym}`), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        const displayable = data.filter(e => e.status === 'Eligible' || e.status === 'Increment Applied');
        setEligibleEmployees(displayable);
        
        if (!isReload) {
          const initialSelected = new Set(displayable.filter(e => e.default_checked && e.status === 'Eligible').map(e => e.id));
          setSelectedEligibleIds(initialSelected);
        } else {
          setSelectedEligibleIds(prev => {
             const newSet = new Set(prev);
             displayable.filter(e => e.status !== 'Eligible').forEach(e => newSet.delete(e.id));
             return newSet;
          });
        }
      }
    } catch (error) {
      console.error(error);
      showMessage('Failed to load eligible employees', 'error');
    } finally {
      setLoadingEligible(false);
    }
  };

  useEffect(() => {
    fetchEligibleEmployees();
  }, [incrementMonth, incrementYear]);

  const handleApplyPreview = () => {
    const percentage = Number(bulkIncrementPercentage);
    const updated = eligibleEmployees.map(emp => {
      if (emp.status !== 'Eligible' || !selectedEligibleIds.has(emp.id)) return emp;
      const currentBasic = Number(emp.current_basic || 0);
      const rawNew = currentBasic + (currentBasic * percentage / 100);
      const roundedNew = Math.ceil(rawNew / 100) * 100;
      return { ...emp, increment_percentage: percentage, increment_amount: roundedNew - currentBasic, revised_basic: roundedNew };
    });
    setEligibleEmployees(updated);
    setShowPreviewModal(false);
  };

  const handleBulkSave = async () => {
    const toSave = eligibleEmployees.filter(emp => selectedEligibleIds.has(emp.id));
    if (toSave.length === 0) {
      showMessage('No employees selected to save', 'error');
      return;
    }
    if (!incrementMonth || !incrementYear) {
      showMessage('Month and Year must be selected', 'error');
      return;
    }
    setSubmitting(true);
    try {
      for (const emp of toSave) {
        let pct = emp.increment_percentage;
        let amt = emp.increment_amount;
        if (!emp.revised_basic) {
            const currentBasic = Number(emp.current_basic || 0);
            pct = Number(bulkIncrementPercentage);
            const rawNew = currentBasic + (currentBasic * pct / 100);
            const roundedNew = Math.ceil(rawNew / 100) * 100;
            amt = roundedNew - currentBasic;
        }
        const response = await fetch(buildApiUrl('/api/v1/increments/approve'), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            employee_id: emp.id,
            increment_type: 'percentage',
            increment_percentage: pct,
            increment_amount: amt,
            applicable_date: effectiveDateForIncrement,
            remarks: `Bulk increment ${incrementMonth}/${incrementYear}`
          }),
        });
        if (response.status === 409) {
          showMessage(`Increment already processed for ${emp.employee_name} in this cycle`, 'error');
          continue;
        } else if (!response.ok) {
          const err = await response.json();
          console.error(`Error saving for ${emp.employee_name}:`, err);
        }
      }
      showMessage('Increments processed successfully', 'success');
      fetchEligibleEmployees(true);
      loadDashboardStats();
    } catch (e) {
      showMessage('Error saving bulk increments', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // --- FUNCTIONAL PROMOTION BULK TAB METHODS ---
  const fetchFpEligibleEmployees = async () => {
    setLoadingFpEligible(true);
    try {
      // Use testing endpoint with ignore_service_rule=true to bypass service rule check
      const response = await fetch(buildApiUrl(`/api/v1/progression/functional-promotion-eligibility-test?ignore_service_rule=true`), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setFpEligibleEmployees(data);
        const initialSelected = new Set(data.filter(e => e.status === 'Eligible').map(e => e.id));
        setSelectedFpIds(initialSelected);
        // Auto-populate percentage from first employee's first milestone config
        if (data.length > 0 && data[0].eligible_milestones?.length > 0) {
          setFpBulkPercentage(data[0].eligible_milestones[0].increase_percentage);
        }
      }
    } catch (error) {
      console.error(error);
      showMessage('Failed to load functional promotion eligible employees', 'error');
    } finally {
      setLoadingFpEligible(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'functional_promotion') {
      fetchFpEligibleEmployees();
    }
  }, [activeTab]);

  const handleFpApplyPreview = () => {
    const percentage = Number(fpBulkPercentage);
    const updated = fpEligibleEmployees.map(emp => {
      if (emp.status !== 'Eligible' || !selectedFpIds.has(emp.id)) return emp;
      const currentBasic = Number(emp.current_basic || 0);
      const rawNew = currentBasic + (currentBasic * percentage / 100);
      const roundedNew = Math.ceil(rawNew / 100) * 100;
      return { ...emp, increment_percentage: percentage, increment_amount: roundedNew - currentBasic, revised_basic: roundedNew };
    });
    setFpEligibleEmployees(updated);
    setShowFpPreviewModal(false);
  };

  const handleFpBulkSave = async () => {
    const toSave = fpEligibleEmployees.filter(emp => selectedFpIds.has(emp.id) && emp.status === 'Eligible');
    if (toSave.length === 0) {
      showMessage('No employees selected to save', 'error');
      return;
    }
    if (!fpEffectiveDate) {
      showMessage('Effective Date is required. Use Preview to set it.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const items = toSave.map(emp => ({
        employee_id: emp.id,
        milestone_years: emp.eligible_milestones[0]?.milestone_years || 0,
        effective_date: fpEffectiveDate,
        remarks: `Bulk functional promotion`,
        increase_percentage: emp.increment_percentage || Number(fpBulkPercentage)
      }));
      const response = await fetch(buildApiUrl('/api/v1/progression/functional-promotions/bulk'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ items }),
      });
      if (response.ok) {
        const result = await response.json();
        showMessage(`Functional promotions processed: ${result.processed} success${result.errors.length > 0 ? `, ${result.errors.length} errors` : ''}`, result.errors.length > 0 ? 'error' : 'success');
        fetchFpEligibleEmployees();
        loadDashboardStats();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to process bulk functional promotions', 'error');
      }
    } catch (e) {
      showMessage('Error saving functional promotions', 'error');
    } finally {
      setSubmitting(false);
    }
  };


  // Load initial data
  useEffect(() => {
    loadDashboardStats();
    loadConfiguration();
    loadHistory();
  }, []);

  // Load employee progression status when employee is selected
  useEffect(() => {
    if (selectedEmployee) {
      loadEmployeeProgressionStatus(selectedEmployee.id);
    }
  }, [selectedEmployee]);

  // Load designations when department changes
  useEffect(() => {
    if (newMapping.department_id) {
      loadDepartmentDesignations(Number(newMapping.department_id));
    } else {
      setFilteredFromDesignations([]);
      setFilteredToDesignations([]);
      setAllDepartmentDesignations([]);
    }
  }, [newMapping.department_id]);

  // Filter To Designations when From Designation changes
  useEffect(() => {
    if (newMapping.from_designation_id) {
      const fromId = Number(newMapping.from_designation_id);
      setFilteredToDesignations(
        allDepartmentDesignations.filter(d => d.value !== fromId)
      );
    } else {
      // If no From Designation selected, show all PROMOTIONAL + ISOLATED
      setFilteredToDesignations(allDepartmentDesignations);
    }
  }, [newMapping.from_designation_id, allDepartmentDesignations]);

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      // Load increment eligibility for regular increment count
      const incrementRes = await fetch(buildApiUrl('/api/v1/increments/eligibility?status=Eligible'), { headers: getAuthHeaders() });
      if (incrementRes.ok) {
        const incrementData = await incrementRes.json();
        setStats(prev => ({ ...prev, regularIncrementDue: incrementData.length || 0 }));
      }
      
      // For functional promotions and promotions processed, we'd need to query the progression endpoints
      // For now, set to 0 as these require employee-specific queries
      setStats(prev => ({ ...prev, functionalPromotionDue: 0, promotionsProcessed: 0 }));
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartmentDesignations = async (departmentId) => {
    try {
      // Load PROMOTIONAL designations for From Designation
      const promotionalDesignations = await fetchDesignations(departmentId, 'PROMOTIONAL');
      setFilteredFromDesignations(promotionalDesignations);
      
      // Load ISOLATED designations for To Designation
      const isolatedDesignations = await fetchDesignations(departmentId, 'ISOLATED');
      
      // Store all department designations (PROMOTIONAL + ISOLATED) for To Designation
      setAllDepartmentDesignations([...promotionalDesignations, ...isolatedDesignations]);
      
      // Initially set To Designations to all (will be filtered when From is selected)
      setFilteredToDesignations([...promotionalDesignations, ...isolatedDesignations]);
    } catch (error) {
      console.error('Error loading department designations:', error);
    }
  };

  const loadConfiguration = async () => {
    setLoadingMappings(true);
    setLoadingConfigs(true);
    try {
      const [mappingsRes, configsRes] = await Promise.all([
        fetch(buildApiUrl('/api/v1/progression/promotion-mappings'), { headers: getAuthHeaders() }),
        fetch(buildApiUrl('/api/v1/progression/functional-promotion-configs'), { headers: getAuthHeaders() })
      ]);
      
      if (mappingsRes.ok) {
        const mappingsData = await mappingsRes.json();
        setPromotionMappings(mappingsData || []);
      }
      
      if (configsRes.ok) {
        const configsData = await configsRes.json();
        setFunctionalConfigs(configsData || []);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    } finally {
      setLoadingMappings(false);
      setLoadingConfigs(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      // Load increment history
      const incrementRes = await fetch(buildApiUrl('/api/v1/increments/history'), { headers: getAuthHeaders() });
      let historyItems = [];
      
      if (incrementRes.ok) {
        const incrementData = await incrementRes.json();
        historyItems = incrementData.map(item => ({
          type: 'Regular Increment',
          date: item.applicable_date,
          details: `Basic ${Number(item.old_basic || 0).toLocaleString('en-IN')} -> ${Number(item.new_basic || 0).toLocaleString('en-IN')}`,
          designation_change: false,
          basic_change: true
        }));
      }
      
      setCombinedHistory(historyItems);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadEmployeeProgressionStatus = async (employeeId) => {
    setLoadingEmployeeStatus(true);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/progression/employee/${employeeId}/status`), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setEmployeeProgressionStatus(data);
        setSelectedDestinationDesigId('');
        setDestinationDepartmentId('');
        setDestinationHeadId('');
        setDepartmentHeadsList([]);
      }
    } catch (error) {
      console.error('Error loading employee progression status:', error);
    } finally {
      setLoadingEmployeeStatus(false);
    }
  };

  const loadHeadsForDepartment = async (departmentId, currentEmpHeadId = null) => {
    if (!departmentId) {
      setDepartmentHeadsList([]);
      setDestinationHeadId('');
      return;
    }
    setLoadingHeads(true);
    try {
      const heads = await fetchDepartmentHeads(departmentId);
      const mappedHeads = heads || [];
      setDepartmentHeadsList(mappedHeads);
      if (currentEmpHeadId && mappedHeads.some((h) => String(h.value) === String(currentEmpHeadId))) {
        setDestinationHeadId(String(currentEmpHeadId));
      } else {
        setDestinationHeadId('');
      }
    } catch (err) {
      console.error('Error fetching heads:', err);
      setDepartmentHeadsList([]);
      setDestinationHeadId('');
    } finally {
      setLoadingHeads(false);
    }
  };

  const handleDestinationDesignationChange = (val) => {
    setSelectedDestinationDesigId(val || '');
    if (!val) {
      setPromotionForm((prev) => ({ ...prev, to_designation_id: '' }));
      setDestinationDepartmentId('');
      setDestinationHeadId('');
      setDepartmentHeadsList([]);
      return;
    }

    const destObj = (employeeProgressionStatus?.valid_promotion_destinations || []).find(
      (d) => String(d.id) === String(val)
    );
    const depts = destObj?.departments || [];
    const currentEmpDeptId = employeeProgressionStatus?.department_id;
    const currentEmpHeadId = employeeProgressionStatus?.payroll_head_id;

    // Check if employee's current department is valid
    const hasCurrentDept = depts.some((d) => Number(d.id) === Number(currentEmpDeptId));

    let chosenDeptId = '';

    if (hasCurrentDept) {
      chosenDeptId = String(currentEmpDeptId);
    } else if (depts.length === 1) {
      chosenDeptId = String(depts[0].id);
    } else {
      chosenDeptId = '';
    }

    let actualDesigId = val;
    if (chosenDeptId) {
      const matchedDept = depts.find((d) => String(d.id) === String(chosenDeptId));
      if (matchedDept?.designation_id) {
        actualDesigId = String(matchedDept.designation_id);
      }
    }

    setPromotionForm((prev) => ({ ...prev, to_designation_id: actualDesigId }));
    setDestinationDepartmentId(chosenDeptId);

    if (chosenDeptId) {
      loadHeadsForDepartment(chosenDeptId, currentEmpHeadId);
    } else {
      setDepartmentHeadsList([]);
      setDestinationHeadId('');
    }
  };

  const handleDestinationDepartmentChange = (newDeptId) => {
    setDestinationDepartmentId(newDeptId);
    if (!newDeptId) {
      setDepartmentHeadsList([]);
      setDestinationHeadId('');
      return;
    }

    const currentEmpHeadId = employeeProgressionStatus?.payroll_head_id;

    if (selectedDestinationObj?.departments) {
      const matchedDept = selectedDestinationObj.departments.find((d) => String(d.id) === String(newDeptId));
      if (matchedDept?.designation_id) {
        setPromotionForm((prev) => ({ ...prev, to_designation_id: String(matchedDept.designation_id) }));
      }
    }

    loadHeadsForDepartment(newDeptId, currentEmpHeadId);
  };

  const searchEmployees = async (query) => {
    if (!query.trim()) {
      setEmployeeSuggestions([]);
      return;
    }
    
    try {
      const response = await fetch(buildApiUrl(`/api/v1/benfish-employees/search?query=${encodeURIComponent(query)}&limit=100`), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setEmployeeSuggestions(data.employees || []);
      }
    } catch (error) {
      console.error('Error searching employees:', error);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchEmployees(employeeQuery);
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [employeeQuery]);

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setEmployeeQuery(`${employee.emp_code} - ${employee.full_name}`);
    setShowEmployeeSuggestions(false);
    setActionType('regular_increment');
    setSelectedDestinationDesigId('');
    setDestinationDepartmentId('');
    setDestinationHeadId('');
    setDepartmentHeadsList([]);
    setHistoricalRows([]);
    setRegularIncrementForm({
      increment_type: 'percentage',
      increment_percentage: 3,
      increment_amount: 0,
      applicable_date: new Date().toISOString().slice(0, 10),
      remarks: ''
    });
    setPromotionForm({
      to_designation_id: '',
      change_basic_pay: false,
      new_basic_salary: '',
      effective_date: new Date().toISOString().slice(0, 10),
      remarks: ''
    });
    setFunctionalPromotionForm({
      milestone_years: '',
      effective_date: new Date().toISOString().slice(0, 10),
      remarks: ''
    });
  };

  const handleRegularIncrementSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) {
      showMessage('Please select an employee first', 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/increments/approve'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...regularIncrementForm,
          employee_id: selectedEmployee.id,
          increment_percentage: Number(regularIncrementForm.increment_percentage || 0),
          increment_amount: Number(regularIncrementForm.increment_amount || 0),
        }),
      });
      
      if (response.ok) {
        showMessage('Regular increment approved successfully', 'success');
        setSelectedEmployee(null);
        setEmployeeQuery('');
        setEmployeeProgressionStatus(null);
        loadDashboardStats();
        loadHistory();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to approve increment', 'error');
      }
    } catch (error) {
      showMessage('Error approving increment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePromotionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) {
      showMessage('Please select an employee first', 'error');
      return;
    }
    if (!selectedDestinationDesigId || !promotionForm.to_designation_id) {
      showMessage('Please select a destination designation', 'error');
      return;
    }
    if (!destinationDepartmentId) {
      showMessage('Please select a destination department', 'error');
      return;
    }

    if (promotionForm.change_basic_pay && !promotionForm.new_basic_salary) {
      showMessage('Please enter new basic salary', 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        employee_id: selectedEmployee.id,
        to_designation_id: Number(promotionForm.to_designation_id),
        change_basic_pay: promotionForm.change_basic_pay,
        effective_date: promotionForm.effective_date,
        remarks: promotionForm.remarks,
        transfer_department_id: Number(destinationDepartmentId),
        transfer_head_id: destinationHeadId ? Number(destinationHeadId) : undefined,
      };
      
      if (promotionForm.change_basic_pay) {
        payload.new_basic_salary = Number(promotionForm.new_basic_salary);
      }
      
      const response = await fetch(buildApiUrl('/api/v1/progression/promotions'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        showMessage('Promotion approved successfully', 'success');
        setSelectedDestinationDesigId('');
        setDestinationDepartmentId('');
        setDestinationHeadId('');
        setDepartmentHeadsList([]);
        loadEmployeeProgressionStatus(selectedEmployee.id);
        setPromotionForm({
          to_designation_id: '',
          change_basic_pay: false,
          new_basic_salary: '',
          effective_date: new Date().toISOString().slice(0, 10),
          remarks: ''
        });
        loadDashboardStats();
        loadHistory();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to approve promotion', 'error');
      }
    } catch (error) {
      showMessage('Error approving promotion', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // --- HISTORICAL PROMOTION HANDLERS ---
  const handleAddHistoricalRow = () => {
    const newRow = {
      tempId: Date.now() + Math.random(),
      from_designation_id: '',
      to_designation_id: '',
      from_department_id: '',
      to_department_id: '',
      from_head_id: '',
      to_head_id: '',
      effective_date: '',
      old_basic_salary: '',
      new_basic_salary: '',
      order_number: '',
      remarks: '',
      toDeptHeads: [],
      fromDeptHeads: [],
      loadingToHeads: false,
      loadingFromHeads: false,
    };
    setHistoricalRows((prev) => [...prev, newRow]);
  };

  const handleUpdateHistoricalRow = (tempId, field, value) => {
    setHistoricalRows((prev) =>
      prev.map((row) => {
        if (row.tempId !== tempId) return row;
        return { ...row, [field]: value };
      })
    );
  };

  const handleRowToDeptChange = async (tempId, deptId) => {
    handleUpdateHistoricalRow(tempId, 'to_department_id', deptId);
    handleUpdateHistoricalRow(tempId, 'to_head_id', '');
    if (!deptId) {
      setHistoricalRows((prev) =>
        prev.map((r) => (r.tempId === tempId ? { ...r, toDeptHeads: [] } : r))
      );
      return;
    }
    try {
      const heads = await fetchDepartmentHeads(deptId);
      setHistoricalRows((prev) =>
        prev.map((r) => (r.tempId === tempId ? { ...r, toDeptHeads: heads || [] } : r))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleRowFromDeptChange = async (tempId, deptId) => {
    handleUpdateHistoricalRow(tempId, 'from_department_id', deptId);
    handleUpdateHistoricalRow(tempId, 'from_head_id', '');
    if (!deptId) {
      setHistoricalRows((prev) =>
        prev.map((r) => (r.tempId === tempId ? { ...r, fromDeptHeads: [] } : r))
      );
      return;
    }
    try {
      const heads = await fetchDepartmentHeads(deptId);
      setHistoricalRows((prev) =>
        prev.map((r) => (r.tempId === tempId ? { ...r, fromDeptHeads: heads || [] } : r))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveHistoricalRow = (tempId) => {
    setHistoricalRows((prev) => prev.filter((row) => row.tempId !== tempId));
  };

  const handleSaveHistoricalPromotions = async () => {
    if (!selectedEmployee) {
      showMessage('Please select an employee first', 'error');
      return;
    }
    if (historicalRows.length === 0) {
      showMessage('Please add at least one past promotion record', 'error');
      return;
    }

    const todayStr = new Date().toISOString().slice(0, 10);
    for (let i = 0; i < historicalRows.length; i++) {
      const r = historicalRows[i];
      if (!r.to_designation_id) {
        showMessage(`Row ${i + 1}: Please select New Designation`, 'error');
        return;
      }
      if (!r.effective_date) {
        showMessage(`Row ${i + 1}: Please enter Effective Date`, 'error');
        return;
      }
      if (r.effective_date > todayStr) {
        showMessage(`Row ${i + 1}: Effective date cannot be in the future for past promotions`, 'error');
        return;
      }
    }

    setSavingHistorical(true);
    try {
      const payload = {
        employee_id: selectedEmployee.id,
        promotions: historicalRows.map((r) => ({
          from_designation_id: r.from_designation_id ? Number(r.from_designation_id) : null,
          to_designation_id: Number(r.to_designation_id),
          from_department_id: r.from_department_id ? Number(r.from_department_id) : null,
          to_department_id: r.to_department_id ? Number(r.to_department_id) : null,
          from_head_id: r.from_head_id ? Number(r.from_head_id) : null,
          to_head_id: r.to_head_id ? Number(r.to_head_id) : null,
          old_basic_salary: r.old_basic_salary !== '' && r.old_basic_salary !== null ? Number(r.old_basic_salary) : null,
          new_basic_salary: r.new_basic_salary !== '' && r.new_basic_salary !== null ? Number(r.new_basic_salary) : null,
          effective_date: r.effective_date,
          order_number: r.order_number?.trim() || null,
          remarks: r.remarks?.trim() || null,
        })),
      };

      const response = await fetch(buildApiUrl('/api/v1/progression/historical-promotions'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save historical promotions');
      }

      showMessage(`${data.length} historical promotion record(s) recorded successfully`, 'success');
      setHistoricalRows([]);
      loadEmployeeProgressionStatus(selectedEmployee.id);
      loadHistory();
    } catch (error) {
      console.error(error);
      showMessage(error.message, 'error');
    } finally {
      setSavingHistorical(false);
    }
  };

  const handleDeleteHistoricalRecord = async (id) => {
    if (!window.confirm('Are you sure you want to delete this historical promotion record?')) return;
    setDeletingHistoricalId(id);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/progression/historical-promotions/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to delete historical record');
      }
      showMessage('Historical promotion record deleted successfully', 'success');
      if (selectedEmployee) {
        loadEmployeeProgressionStatus(selectedEmployee.id);
      }
      loadHistory();
    } catch (err) {
      console.error(err);
      showMessage(err.message, 'error');
    } finally {
      setDeletingHistoricalId(null);
    }
  };

  const handleFunctionalPromotionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) {
      showMessage('Please select an employee first', 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/progression/functional-promotions'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          employee_id: selectedEmployee.id,
          milestone_years: Number(functionalPromotionForm.milestone_years),
          effective_date: functionalPromotionForm.effective_date,
          remarks: functionalPromotionForm.remarks
        }),
      });
      
      if (response.ok) {
        showMessage('Functional promotion approved successfully', 'success');
        setSelectedEmployee(null);
        setEmployeeQuery('');
        setEmployeeProgressionStatus(null);
        loadDashboardStats();
        loadHistory();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to approve functional promotion', 'error');
      }
    } catch (error) {
      showMessage('Error approving functional promotion', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMapping = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/progression/promotion-mappings'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          department_id: Number(newMapping.department_id),
          from_designation_id: Number(newMapping.from_designation_id),
          to_designation_id: Number(newMapping.to_designation_id),
          is_active: true
        }),
      });
      
      if (response.ok) {
        showMessage('Promotion mapping added successfully', 'success');
        setNewMapping({ department_id: '', from_designation_id: '', to_designation_id: '' });
        setFilteredFromDesignations([]);
        setFilteredToDesignations([]);
        loadConfiguration();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to add mapping', 'error');
      }
    } catch (error) {
      showMessage('Error adding mapping', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleMapping = async (mappingId, currentStatus) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/progression/promotion-mappings/${mappingId}?is_active=${!currentStatus}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        showMessage('Mapping updated successfully', 'success');
        loadConfiguration();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to update mapping', 'error');
      }
    } catch (error) {
      showMessage('Error updating mapping', 'error');
    }
  };

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const renderDashboard = () => (
    <div className="grid gap-4 md:grid-cols-4">
      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
        <p className="text-sm text-gray-500">Regular Increment Due</p>
        <p className="text-2xl font-semibold text-gray-900">{stats.regularIncrementDue}</p>
      </div>
      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
        <p className="text-sm text-gray-500">Functional Promotion Due</p>
        <p className="text-2xl font-semibold text-gray-900">{stats.functionalPromotionDue}</p>
      </div>
      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
        <p className="text-sm text-gray-500">Promotions Processed</p>
        <p className="text-2xl font-semibold text-gray-900">{stats.promotionsProcessed}</p>
      </div>
      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
        <p className="text-sm text-gray-500">Progression History</p>
        <p className="text-2xl font-semibold text-gray-900">{stats.totalHistory}</p>
      </div>
    </div>
  );

  const renderIncrementTab = () => {
    const filteredEmployees = eligibleEmployees.filter(e => 
      e.employee_code?.toLowerCase().includes(incrementSearch.toLowerCase()) || 
      e.employee_name?.toLowerCase().includes(incrementSearch.toLowerCase())
    );

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Increment Cycle</h3>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Increment Month</label>
              <select
                value={incrementMonth}
                onChange={(e) => setIncrementMonth(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2"
              >
                <option value="">Select Month</option>
                <option value="01">January</option>
                <option value="07">July</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Year</label>
              <select
                value={incrementYear}
                onChange={(e) => setIncrementYear(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2"
              >
                <option value="">Select Year</option>
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Effective Date</label>
              <input
                type="text"
                readOnly
                value={incrementMonth && incrementYear ? new Date(incrementYear, parseInt(incrementMonth) - 1, 1).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 bg-gray-50 text-gray-500"
                placeholder="Derived automatically"
              />
            </div>
          </div>
        </div>

        {incrementMonth && incrementYear && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-1/3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={incrementSearch}
                    onChange={(e) => setIncrementSearch(e.target.value)}
                    placeholder="Search employees..."
                    className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowPreviewModal(true)}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={selectedEligibleIds.size === 0}
                >
                  Preview
                </button>
                <button
                  type="button"
                  onClick={handleBulkSave}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={submitting || selectedEligibleIds.size === 0}
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {loadingEligible ? (
              <p className="text-sm text-gray-500">Loading eligible employees...</p>
            ) : filteredEmployees.length === 0 ? (
              <p className="text-sm text-gray-500">No eligible employees found.</p>
            ) : (
              <div className="overflow-x-auto overflow-y-auto max-h-[500px] rounded-lg border border-gray-200">
                <table className="w-full text-left text-sm text-gray-600 relative">
                  <thead className="bg-gray-50 text-gray-700 sticky top-0 z-10 shadow-sm">
                    <tr>
                      <th className="px-4 py-3 bg-gray-50">
                        <input
                          type="checkbox"
                          checked={
                            eligibleEmployees.filter(e => e.status === 'Eligible' && e.default_checked).length > 0 &&
                            selectedEligibleIds.size === eligibleEmployees.filter(e => e.status === 'Eligible' && e.default_checked).length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEligibleIds(new Set(eligibleEmployees.filter(emp => emp.status === 'Eligible' && emp.default_checked).map(emp => emp.id)));
                            } else {
                              setSelectedEligibleIds(new Set());
                            }
                          }}
                        />
                      </th>
                      <th className="px-4 py-3">Code</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Department</th>
                      <th className="px-4 py-3">Designation</th>
                      <th className="px-4 py-3 text-right">Current Basic</th>
                      <th className="px-4 py-3 text-right">Last Increment</th>
                      <th className="px-4 py-3 text-right">Inc %</th>
                      <th className="px-4 py-3 text-right">Inc Amount</th>
                      <th className="px-4 py-3 text-right">Revised Basic</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEmployees.map((emp) => (
                      <tr key={emp.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {emp.status === 'Increment Applied' ? (
                            <CheckCircle className="h-4 w-4 text-green-500 inline-block" />
                          ) : (
                            <input
                              type="checkbox"
                              checked={selectedEligibleIds.has(emp.id)}
                              onChange={(e) => {
                                const newSet = new Set(selectedEligibleIds);
                                if (e.target.checked) newSet.add(emp.id);
                                else newSet.delete(emp.id);
                                setSelectedEligibleIds(newSet);
                              }}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{emp.employee_code}</td>
                        <td className="px-4 py-3">{emp.employee_name}</td>
                        <td className="px-4 py-3">{emp.department_name || '—'}</td>
                        <td className="px-4 py-3">{emp.designation_name || '—'}</td>
                        <td className="px-4 py-3 text-right">₹{Number(emp.current_basic || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-right group relative cursor-help">
                          {emp.last_increment ? emp.last_increment.label : '—'}
                          {emp.increment_history && emp.increment_history.length > 0 && (
                            <div className="absolute z-20 hidden group-hover:block bg-gray-900 text-white text-xs rounded p-2 bottom-full right-0 mb-1 w-max shadow-lg">
                              <p className="font-semibold mb-1 border-b border-gray-700 pb-1">History</p>
                              {emp.increment_history.map((h, i) => (
                                <div key={i}>{h.label}</div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {(selectedEligibleIds.has(emp.id) || emp.status === 'Increment Applied') && emp.increment_percentage ? emp.increment_percentage : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {(selectedEligibleIds.has(emp.id) || emp.status === 'Increment Applied') && emp.increment_amount ? `₹${Number(emp.increment_amount).toLocaleString('en-IN')}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-blue-600">
                          {(selectedEligibleIds.has(emp.id) || emp.status === 'Increment Applied') && emp.revised_basic ? `₹${Number(emp.revised_basic).toLocaleString('en-IN')}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Preview Modal Overlay */}
        {showPreviewModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
              <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Preview Increment</h3>
                <button onClick={() => setShowPreviewModal(false)} className="text-gray-400 hover:text-gray-600">&times;</button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                    <p className="text-sm text-gray-500">Total Employees</p>
                    <p className="text-xl font-semibold">{eligibleEmployees.length}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-100 text-center">
                    <p className="text-sm text-green-600">Checked</p>
                    <p className="text-xl font-semibold text-green-700">{selectedEligibleIds.size}</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-100 text-center">
                    <p className="text-sm text-red-600">Unchecked</p>
                    <p className="text-xl font-semibold text-red-700">{eligibleEmployees.length - selectedEligibleIds.size}</p>
                  </div>
                </div>
                
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="mb-1 block text-sm font-medium text-gray-700">Select Increment Percentage</label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={bulkIncrementPercentage}
                        onChange={(e) => setBulkIncrementPercentage(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
                <button onClick={() => setShowPreviewModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">Cancel</button>
                <button onClick={handleApplyPreview} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Apply</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  const renderFunctionalPromotionTab = () => {
    const filteredFpEmployees = fpEligibleEmployees.filter(e =>
      e.employee_code?.toLowerCase().includes(fpSearch.toLowerCase()) ||
      e.employee_name?.toLowerCase().includes(fpSearch.toLowerCase())
    );

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-1/3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={fpSearch}
                  onChange={(e) => setFpSearch(e.target.value)}
                  placeholder="Search employees..."
                  className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowFpPreviewModal(true)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled={selectedFpIds.size === 0}
              >
                Preview
              </button>
              <button
                type="button"
                onClick={handleFpBulkSave}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={submitting || selectedFpIds.size === 0}
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {loadingFpEligible ? (
            <p className="text-sm text-gray-500">Loading eligible employees...</p>
          ) : filteredFpEmployees.length === 0 ? (
            <p className="text-sm text-gray-500">No eligible employees found.</p>
          ) : (
            <div className="overflow-x-auto overflow-y-auto max-h-[500px] rounded-lg border border-gray-200">
              <table className="w-full text-left text-sm text-gray-600 relative">
                <thead className="bg-gray-50 text-gray-700 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="px-4 py-3 bg-gray-50">
                      <input
                        type="checkbox"
                        checked={
                          fpEligibleEmployees.filter(e => e.status === 'Eligible').length > 0 &&
                          selectedFpIds.size === fpEligibleEmployees.filter(e => e.status === 'Eligible').length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedFpIds(new Set(fpEligibleEmployees.filter(emp => emp.status === 'Eligible').map(emp => emp.id)));
                          } else {
                            setSelectedFpIds(new Set());
                          }
                        }}
                      />
                    </th>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Department</th>
                    <th className="px-4 py-3">Designation</th>
                    <th className="px-4 py-3">Completion date</th>
                    <th className="px-4 py-3 text-right">Current Basic</th>
                    {/* <th className="px-4 py-3 text-center">Qualifying Years</th> */}
                    <th className="px-4 py-3 text-right">Inc %</th>
                    <th className="px-4 py-3 text-right">Inc Amount</th>
                    <th className="px-4 py-3 text-right">Revised Basic</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredFpEmployees.map((emp) => (
                    <tr key={emp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {emp.status === 'Applied' ? (
                          <CheckCircle className="h-4 w-4 text-green-500 inline-block" />
                        ) : emp.status === 'Held' ? (
                          <Clock className="h-4 w-4 text-yellow-500 inline-block" />
                        ) : (
                          <input
                            type="checkbox"
                            checked={selectedFpIds.has(emp.id)}
                            onChange={(e) => {
                              const newSet = new Set(selectedFpIds);
                              if (e.target.checked) newSet.add(emp.id);
                              else newSet.delete(emp.id);
                              setSelectedFpIds(newSet);
                            }}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{emp.employee_code}</td>
                      <td className="px-4 py-3">{emp.employee_name}</td>
                      <td className="px-4 py-3">{emp.department_name || '—'}</td>
                      <td className="px-4 py-3">{emp.designation_name || '—'}</td>
                      <td className="px-4 py-3">
                        {formatDate(emp.eligible_milestones?.[0]?.completion_date, '—')}
                      </td>
                      <td className="px-4 py-3 text-right">₹{Number(emp.current_basic || 0).toLocaleString('en-IN')}</td>
                      {/* <td className="px-4 py-3 text-center">{emp.qualifying_service_years}</td> */}
                      <td className="px-4 py-3 text-right">
                        {(selectedFpIds.has(emp.id) || emp.status === 'Applied') && emp.increment_percentage ? emp.increment_percentage : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {(selectedFpIds.has(emp.id) || emp.status === 'Applied') && emp.increment_amount ? `₹${Number(emp.increment_amount).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-blue-600">
                        {(selectedFpIds.has(emp.id) || emp.status === 'Applied') && emp.revised_basic ? `₹${Number(emp.revised_basic).toLocaleString('en-IN')}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* FP Preview Modal */}
        {showFpPreviewModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
              <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Preview Functional Promotion</h3>
                <button onClick={() => setShowFpPreviewModal(false)} className="text-gray-400 hover:text-gray-600">&times;</button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 text-center">
                    <p className="text-sm text-gray-500">Total Employees</p>
                    <p className="text-xl font-semibold">{fpEligibleEmployees.length}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex flex-col justify-between items-center text-center">
                    <div>
                      <p className="text-sm text-green-600 font-medium">Checked</p>
                      <p className="text-xl font-semibold text-green-700">{selectedFpIds.size}</p>
                    </div>
                    {selectedFpIds.size > 0 && (
                      <button 
                        type="button" 
                        onClick={() => setShowViewSelectedFpModal(true)} 
                        className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800 underline focus:outline-none"
                      >
                        View List
                      </button>
                    )}
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-100 text-center">
                    <p className="text-sm text-red-600">Unchecked</p>
                    <p className="text-xl font-semibold text-red-700">{fpEligibleEmployees.length - selectedFpIds.size}</p>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Effective From</label>
                  <input
                    type="date"
                    value={fpEffectiveDate}
                    onChange={(e) => setFpEffectiveDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Functional Promotion Percentage</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      value={fpBulkPercentage}
                      onChange={(e) => setFpBulkPercentage(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
                <button onClick={() => setShowFpPreviewModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">Cancel</button>
                <button onClick={handleFpApplyPreview} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Apply</button>
              </div>
            </div>
          </div>
        )}

        {/* View Selected Employees Modal */}
        {showViewSelectedFpModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
              <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Selected Employees ({selectedFpIds.size})</h3>
                <button onClick={() => setShowViewSelectedFpModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">&times;</button>
              </div>
              <div className="p-6 max-h-96 overflow-y-auto space-y-2">
                {fpEligibleEmployees
                  .filter((emp) => selectedFpIds.has(emp.id))
                  .map((emp) => (
                    <div key={emp.id} className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-lg border border-gray-100">
                      <div>
                        <p className="font-semibold text-gray-950 text-sm">{emp.employee_name}</p>
                        <p className="text-xs text-gray-500">{emp.department_name || 'No Dept'} &bull; {emp.designation_name || 'No Desig'}</p>
                      </div>
                      <span className="bg-blue-50 text-blue-800 text-xs font-semibold px-2.5 py-1 rounded border border-blue-100">
                        {emp.employee_code}
                      </span>
                    </div>
                  ))}
              </div>
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end">
                <button onClick={() => setShowViewSelectedFpModal(false)} className="rounded-lg bg-gray-800 text-white px-4 py-2 text-sm font-medium hover:bg-gray-900">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEmployeeActionTab = (tabType) => {
    const currentActionType = tabType;
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Employee</h3>
          <div className="relative">
            <input
              type="text"
              value={employeeQuery}
              onChange={(e) => {
                setEmployeeQuery(e.target.value);
                setShowEmployeeSuggestions(true);
              }}
              onFocus={() => setShowEmployeeSuggestions(true)}
              onBlur={() => setTimeout(() => setShowEmployeeSuggestions(false), 150)}
              placeholder="Search by employee code or name..."
              className="w-full rounded-lg border border-gray-200 px-4 py-3"
            />
            {showEmployeeSuggestions && employeeSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {employeeSuggestions.map((emp) => (
                  <button
                    key={emp.id}
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { handleEmployeeSelect(emp); setActionType(currentActionType); }}
                  >
                    <span>{emp.emp_code} - {emp.full_name}</span>
                    <span className="text-xs text-gray-400">{emp.department_name || '—'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedEmployee && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            {loadingEmployeeStatus ? (
              <p className="text-sm text-gray-500">Loading employee details...</p>
            ) : employeeProgressionStatus ? (
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Details</h3>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div><p className="text-sm text-gray-500">Employee Code</p><p className="font-medium text-gray-900">{employeeProgressionStatus.employee_code}</p></div>
                    <div><p className="text-sm text-gray-500">Employee Name</p><p className="font-medium text-gray-900">{employeeProgressionStatus.employee_name}</p></div>
                    <div><p className="text-sm text-gray-500">Department</p><p className="font-medium text-gray-900">{employeeProgressionStatus.department_name}</p></div>
                    <div><p className="text-sm text-gray-500">Current Designation</p><p className="font-medium text-gray-900">{employeeProgressionStatus.current_designation}</p></div>
                    <div><p className="text-sm text-gray-500">Designation Type</p><p className="font-medium text-gray-900">{employeeProgressionStatus.current_designation_type || '—'}</p></div>
                    <div>
                      <p className="text-sm text-gray-500">Current Basic</p>
                      {employeeProgressionStatus.basic_pay_available ? (
                        <p className="font-medium text-gray-900">₹{Number(employeeProgressionStatus.current_basic_salary || 0).toLocaleString('en-IN')}</p>
                      ) : (
                        <div>
                          <p className="font-medium text-red-600">Not Configured</p>
                          {employeeProgressionStatus.basic_pay_error && <p className="text-xs text-red-500 mt-1">{employeeProgressionStatus.basic_pay_error}</p>}
                        </div>
                      )}
                    </div>
                    <div><p className="text-sm text-gray-500">Qualifying Service</p><p className="font-medium text-gray-900">{employeeProgressionStatus.qualifying_service_years} Years</p></div>
                  </div>
                </div>

                {currentActionType === 'promotion' && (
                  employeeProgressionStatus.current_designation_type === 'ISOLATED' ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <p className="text-sm font-medium text-amber-800">
                        ⚠ This employee belongs to an Isolated designation. 
                        Promotion is not applicable.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handlePromotionSubmit} className="space-y-4">
                      <h4 className="font-medium text-gray-900">Promotion Details</h4>
                      {employeeProgressionStatus.functional_promotion_history.length > 0 && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                          <p className="font-medium text-blue-900 mb-2">Financial Progression Status</p>
                          {employeeProgressionStatus.functional_promotion_history.map((fp) => (
                            <div key={fp.id} className="text-sm text-blue-800">{fp.milestone_years}-Year Functional Promotion: Granted</div>
                          ))}
                        </div>
                      )}

                      <div className="grid gap-4 md:grid-cols-2">
                        {/* A. New Designation */}
                        <div className="md:col-span-2">
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            New Designation <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            value={selectedDestinationDesigId}
                            onChange={handleDestinationDesignationChange}
                            options={promotionDestinationOptions}
                            placeholder="Select destination designation"
                            required
                            disabled={!employeeProgressionStatus.basic_pay_available || promotionDestinationOptions.length === 0}
                            isClearable
                          />
                          {promotionDestinationOptions.length === 0 && (
                            <p className="mt-1 text-xs text-gray-500">No valid promotion destinations available for this employee</p>
                          )}
                        </div>

                        {/* B. Department */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Department <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={destinationDepartmentId}
                            onChange={(e) => handleDestinationDepartmentChange(e.target.value)}
                            disabled={!selectedDestinationDesigId || availableDepartmentsForSelectedDesig.length === 0}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                            required
                          >
                            <option value="">Select Department</option>
                            {availableDepartmentsForSelectedDesig.map((dept) => (
                              <option key={dept.id} value={String(dept.id)}>
                                {dept.name}{dept.code ? ` (${dept.code})` : ''}
                              </option>
                            ))}
                          </select>
                          {selectedDestinationDesigId && availableDepartmentsForSelectedDesig.length === 0 && (
                            <p className="mt-1 text-xs text-amber-600">No departments available for this designation</p>
                          )}
                        </div>

                        {/* C. Head */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Head
                          </label>
                          <select
                            value={destinationHeadId}
                            onChange={(e) => setDestinationHeadId(e.target.value)}
                            disabled={!destinationDepartmentId || loadingHeads}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                          >
                            {loadingHeads ? (
                              <option value="">Loading heads...</option>
                            ) : (
                              <>
                                <option value="">Select Head (Optional)</option>
                                {departmentHeadsList.map((h) => (
                                  <option key={h.value} value={String(h.value)}>
                                    {h.label}
                                  </option>
                                ))}
                              </>
                            )}
                          </select>
                          {destinationDepartmentId && !loadingHeads && departmentHeadsList.length === 0 && (
                            <p className="mt-1 text-xs text-gray-500">No heads configured for this department</p>
                          )}
                        </div>

                        {/* Change Basic Pay */}
                        <div className="md:col-span-2">
                          <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-3 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={promotionForm.change_basic_pay}
                              onChange={(e) => setPromotionForm({ ...promotionForm, change_basic_pay: e.target.checked })}
                            />
                            Change Basic Pay
                          </label>
                        </div>

                        {/* New Basic Salary */}
                        {promotionForm.change_basic_pay && (
                          <div className="md:col-span-2">
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              New Basic Salary <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={promotionForm.new_basic_salary}
                              onChange={(e) => setPromotionForm({ ...promotionForm, new_basic_salary: e.target.value })}
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                              placeholder="Enter new basic salary"
                              required
                            />
                          </div>
                        )}

                        {/* Effective Date */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">
                            Effective Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={promotionForm.effective_date}
                            onChange={(e) => setPromotionForm({ ...promotionForm, effective_date: e.target.value })}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            required
                          />
                        </div>

                        {/* Remarks */}
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Remarks</label>
                          <textarea
                            value={promotionForm.remarks}
                            onChange={(e) => setPromotionForm({ ...promotionForm, remarks: e.target.value })}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            rows="2"
                            placeholder="Enter optional promotion remarks"
                          />
                        </div>

                        {/* Submit Button */}
                        <div className="md:col-span-2">
                          <button
                            type="submit"
                            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                            disabled={
                              submitting ||
                              !selectedDestinationDesigId ||
                              !destinationDepartmentId ||
                              !employeeProgressionStatus.basic_pay_available ||
                              (promotionForm.change_basic_pay && !promotionForm.new_basic_salary)
                            }
                          >
                            {submitting ? 'Submitting...' : 'Approve Promotion'}
                          </button>
                        </div>
                      </div>
                    </form>
                  )
                )}

                {/* Historical Promotions Section (Record Past Promotions) */}
                {currentActionType === 'promotion' && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900 text-base">Historical Promotions</h4>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                            Record-Only / Service Book
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Record past promotions that occurred prior to HRMS for historical documentation and Service Book records.
                          These entries will never alter the employee's current active post, department, or payroll.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddHistoricalRow}
                        className="inline-flex items-center gap-1.5 self-start sm:self-auto rounded-lg bg-indigo-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-indigo-700 shadow-sm transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        Add Past Promotion
                      </button>
                    </div>

                    {historicalRows.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/75 p-6 text-center">
                        <History className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-sm font-medium text-gray-700">No past promotion rows added</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Click the "Add Past Promotion" button above if you need to document historical promotions for this employee.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {historicalRows.map((row, index) => (
                          <div key={row.tempId} className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-4 shadow-sm relative">
                            <div className="flex items-center justify-between pb-3 mb-3 border-b border-indigo-100">
                              <div className="flex items-center gap-2">
                                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold">
                                  {index + 1}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">Past Promotion Entry #{index + 1}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveHistoricalRow(row.tempId)}
                                className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Remove
                              </button>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                              {/* Previous Designation */}
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  Previous Designation <span className="text-gray-400">(Optional)</span>
                                </label>
                                <SearchableSelect
                                  value={row.from_designation_id}
                                  onChange={(val) => handleUpdateHistoricalRow(row.tempId, 'from_designation_id', val)}
                                  options={allDesignationOptions}
                                  placeholder="Select previous designation"
                                  isClearable
                                />
                              </div>

                              {/* New Designation */}
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  New / Promoted Designation <span className="text-red-500">*</span>
                                </label>
                                <SearchableSelect
                                  value={row.to_designation_id}
                                  onChange={(val) => handleUpdateHistoricalRow(row.tempId, 'to_designation_id', val)}
                                  options={allDesignationOptions}
                                  placeholder="Select promoted designation"
                                  required
                                  isClearable
                                />
                              </div>

                              {/* Effective Date */}
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  Effective Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="date"
                                  max={new Date().toISOString().slice(0, 10)}
                                  value={row.effective_date}
                                  onChange={(e) => handleUpdateHistoricalRow(row.tempId, 'effective_date', e.target.value)}
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                                  required
                                />
                              </div>

                              {/* Previous Department */}
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  Previous Department <span className="text-gray-400">(Optional)</span>
                                </label>
                                <select
                                  value={row.from_department_id}
                                  onChange={(e) => handleRowFromDeptChange(row.tempId, e.target.value)}
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                                >
                                  <option value="">Select Department</option>
                                  {allDepartmentList.map((dept) => (
                                    <option key={dept.value} value={dept.value}>
                                      {dept.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Promoted Department */}
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  Promoted Department <span className="text-gray-400">(Optional)</span>
                                </label>
                                <select
                                  value={row.to_department_id}
                                  onChange={(e) => handleRowToDeptChange(row.tempId, e.target.value)}
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                                >
                                  <option value="">Select Department</option>
                                  {allDepartmentList.map((dept) => (
                                    <option key={dept.value} value={dept.value}>
                                      {dept.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Promoted Head */}
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  Promoted Head <span className="text-gray-400">(Optional)</span>
                                </label>
                                <select
                                  value={row.to_head_id}
                                  onChange={(e) => handleUpdateHistoricalRow(row.tempId, 'to_head_id', e.target.value)}
                                  disabled={!row.to_department_id}
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                >
                                  <option value="">Select Head (Optional)</option>
                                  {(row.toDeptHeads || []).map((h) => (
                                    <option key={h.value} value={String(h.value)}>
                                      {h.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              {/* Basic Pay Before */}
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  Basic Pay Before (₹) <span className="text-gray-400">(Optional)</span>
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={row.old_basic_salary}
                                  onChange={(e) => handleUpdateHistoricalRow(row.tempId, 'old_basic_salary', e.target.value)}
                                  placeholder="e.g. 25000"
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                                />
                              </div>

                              {/* Basic Pay After */}
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  Basic Pay After (₹) <span className="text-gray-400">(Optional)</span>
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={row.new_basic_salary}
                                  onChange={(e) => handleUpdateHistoricalRow(row.tempId, 'new_basic_salary', e.target.value)}
                                  placeholder="e.g. 30000"
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                                />
                              </div>

                              {/* Order / Ref Number */}
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  Order / Reference No. <span className="text-gray-400">(Optional)</span>
                                </label>
                                <input
                                  type="text"
                                  value={row.order_number}
                                  onChange={(e) => handleUpdateHistoricalRow(row.tempId, 'order_number', e.target.value)}
                                  placeholder="e.g. ORD/2021/045"
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                                />
                              </div>

                              {/* Remarks */}
                              <div className="sm:col-span-2 lg:col-span-3">
                                <label className="mb-1 block text-xs font-medium text-gray-700">
                                  Remarks / Notes <span className="text-gray-400">(Optional)</span>
                                </label>
                                <input
                                  type="text"
                                  value={row.remarks}
                                  onChange={(e) => handleUpdateHistoricalRow(row.tempId, 'remarks', e.target.value)}
                                  placeholder="e.g. Promoted as per 5-year seniority review"
                                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <div className="flex items-center justify-end gap-3 pt-2">
                          <button
                            type="button"
                            onClick={() => setHistoricalRows([])}
                            disabled={savingHistorical}
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            Discard
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveHistoricalPromotions}
                            disabled={savingHistorical}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:bg-gray-400 transition-colors shadow-sm"
                          >
                            {savingHistorical ? 'Saving Records...' : 'Save Historical Records'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Promotion History Section (Rendered regardless of isolated designation if action is promotion) */}
                {currentActionType === 'promotion' && (
                  <div>
                    <div className="flex items-center justify-between mb-4 mt-8 pt-6 border-t border-gray-200">
                      <div>
                        <h4 className="font-medium text-gray-900">Promotion History</h4>
                        <p className="text-xs text-gray-500 mt-0.5">Unified history of approved HRMS promotions and recorded past promotions</p>
                      </div>
                    </div>
                    {employeeProgressionStatus.promotion_history && employeeProgressionStatus.promotion_history.length > 0 ? (
                      <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-600">
                            <tr>
                              <th className="px-4 py-3 text-left">Effective Date</th>
                              <th className="px-4 py-3 text-left">Type</th>
                              <th className="px-4 py-3 text-left">From Designation</th>
                              <th className="px-4 py-3 text-left">To Designation</th>
                              <th className="px-4 py-3 text-left">From Department</th>
                              <th className="px-4 py-3 text-left">To Department</th>
                              <th className="px-4 py-3 text-left">Basic Pay</th>
                              <th className="px-4 py-3 text-left">Order No. / Remarks</th>
                              <th className="px-4 py-3 text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white text-sm text-gray-700">
                            {employeeProgressionStatus.promotion_history.map((promo, index) => (
                              <tr key={promo.id ? `${promo.is_historical ? 'hist' : 'active'}-${promo.id}` : index} className="hover:bg-gray-50">
                                <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-900 font-medium">
                                  {formatDate(promo.effective_date, '-')}
                                </td>
                                <td className="px-4 py-3.5 whitespace-nowrap">
                                  {promo.is_historical || promo.status === 'historical' ? (
                                    <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-800 border border-violet-200">
                                      Historical
                                    </span>
                                  ) : (
                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                      promo.status === 'approved' ? 'bg-green-100 text-green-800' : 
                                      promo.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {promo.status}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3.5 text-sm">{promo.from_designation || '-'}</td>
                                <td className="px-4 py-3.5 text-sm font-medium text-green-700">{promo.to_designation || '-'}</td>
                                <td className="px-4 py-3.5 text-sm">{promo.from_department || '-'}</td>
                                <td className="px-4 py-3.5 text-sm font-medium text-green-700">{promo.to_department || '-'}</td>
                                <td className="px-4 py-3.5 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {promo.new_basic_salary ? `₹${promo.new_basic_salary.toLocaleString('en-IN')}` : '-'}
                                </td>
                                <td className="px-4 py-3.5 text-xs text-gray-600 max-w-xs">
                                  {promo.order_number && (
                                    <div className="font-semibold text-indigo-700">Ref: {promo.order_number}</div>
                                  )}
                                  {promo.remarks ? <div className="truncate" title={promo.remarks}>{promo.remarks}</div> : (!promo.order_number && '-')}
                                </td>
                                <td className="px-4 py-3.5 text-center whitespace-nowrap">
                                  {promo.is_historical || promo.status === 'historical' ? (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteHistoricalRecord(promo.id)}
                                      disabled={deletingHistoricalId === promo.id}
                                      title="Delete Historical Record"
                                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  ) : (
                                    <span className="text-gray-400 text-xs">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                        No promotion history available.
                      </div>
                    )}
                  </div>
                )}

                {currentActionType === 'functional_promotion' && (
                  <form onSubmit={handleFunctionalPromotionSubmit} className="space-y-4">
                    <h4 className="font-medium text-gray-900">Functional Promotion Details</h4>
                    {employeeProgressionStatus.functional_promotion_eligibility.length === 0 ? (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">Employee is not currently eligible for any functional promotion milestones.</div>
                    ) : (
                      <>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Eligible Milestone</label>
                          <select value={functionalPromotionForm.milestone_years} onChange={(e) => setFunctionalPromotionForm({ ...functionalPromotionForm, milestone_years: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3 py-2" required>
                            <option value="">Select milestone</option>
                            {employeeProgressionStatus.functional_promotion_eligibility.map((milestone) => (
                              <option key={milestone.milestone_years} value={milestone.milestone_years}>{milestone.milestone_years} Years ({milestone.increase_percentage}% increase)</option>
                            ))}
                          </select>
                        </div>
                        {functionalPromotionForm.milestone_years && (
                          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                            <p className="text-sm text-green-800">
                              Configured Benefit: {employeeProgressionStatus.functional_promotion_eligibility.find(m => m.milestone_years === Number(functionalPromotionForm.milestone_years))?.increase_percentage}% increase
                            </p>
                          </div>
                        )}
                        <div className="grid gap-4 md:grid-cols-2">
                          <div><label className="mb-1 block text-sm font-medium text-gray-700">Effective Date</label><input type="date" value={functionalPromotionForm.effective_date} onChange={(e) => setFunctionalPromotionForm({ ...functionalPromotionForm, effective_date: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3 py-2" required /></div>
                          <div><label className="mb-1 block text-sm font-medium text-gray-700">Remarks</label><textarea value={functionalPromotionForm.remarks} onChange={(e) => setFunctionalPromotionForm({ ...functionalPromotionForm, remarks: e.target.value })} className="w-full rounded-lg border border-gray-200 px-3 py-2" rows="2" /></div>
                        </div>
                        <button type="submit" className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-400" disabled={submitting || !employeeProgressionStatus.basic_pay_available}>
                          {submitting ? 'Submitting...' : 'Approve Functional Promotion'}
                        </button>
                      </>
                    )}
                  </form>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  // eslint-disable-next-line no-unused-vars
  const _old_renderProcessProgression = () => (
    <div className="space-y-6">
      {/* Employee Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Employee</h3>
        <div className="relative">
          <input
            type="text"
            value={employeeQuery}
            onChange={(e) => {
              setEmployeeQuery(e.target.value);
              setShowEmployeeSuggestions(true);
            }}
            onFocus={() => setShowEmployeeSuggestions(true)}
            onBlur={() => setTimeout(() => setShowEmployeeSuggestions(false), 150)}
            placeholder="Search by employee code or name..."
            className="w-full rounded-lg border border-gray-200 px-4 py-3"
          />
          {showEmployeeSuggestions && employeeSuggestions.length > 0 && (
            <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
              {employeeSuggestions.map((emp) => (
                <button
                  key={emp.id}
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleEmployeeSelect(emp)}
                >
                  <span>{emp.emp_code} - {emp.full_name}</span>
                  <span className="text-xs text-gray-400">{emp.department_name || '—'}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Employee Details and Action Form */}
      {selectedEmployee && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {loadingEmployeeStatus ? (
            <p className="text-sm text-gray-500">Loading employee details...</p>
          ) : employeeProgressionStatus ? (
            <div className="space-y-6">
              {/* Employee Details */}
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Details</h3>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <p className="text-sm text-gray-500">Employee Code</p>
                    <p className="font-medium text-gray-900">{employeeProgressionStatus.employee_code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Employee Name</p>
                    <p className="font-medium text-gray-900">{employeeProgressionStatus.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Department</p>
                    <p className="font-medium text-gray-900">{employeeProgressionStatus.department_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Current Designation</p>
                    <p className="font-medium text-gray-900">{employeeProgressionStatus.current_designation}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Designation Type</p>
                    <p className="font-medium text-gray-900">{employeeProgressionStatus.current_designation_type || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Current Basic</p>
                    {employeeProgressionStatus.basic_pay_available ? (
                      <p className="font-medium text-gray-900">₹{Number(employeeProgressionStatus.current_basic_salary || 0).toLocaleString('en-IN')}</p>
                    ) : (
                      <div>
                        <p className="font-medium text-red-600">Not Configured</p>
                        {employeeProgressionStatus.basic_pay_error && (
                          <p className="text-xs text-red-500 mt-1">{employeeProgressionStatus.basic_pay_error}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Qualifying Service</p>
                    <p className="font-medium text-gray-900">{employeeProgressionStatus.qualifying_service_years} Years</p>
                  </div>
                </div>
              </div>

              {/* Action Type Selection */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Action Type</h3>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setActionType('regular_increment')}
                    className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium ${
                      actionType === 'regular_increment'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Regular Increment
                  </button>
                  <button
                    type="button"
                    onClick={() => setActionType('promotion')}
                    className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium ${
                      actionType === 'promotion'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    disabled={employeeProgressionStatus.current_designation_type === 'ISOLATED'}
                  >
                    Promotion
                  </button>
                  <button
                    type="button"
                    onClick={() => setActionType('functional_promotion')}
                    className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium ${
                      actionType === 'functional_promotion'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Functional Promotion
                  </button>
                </div>
              </div>

              {/* Isolated Designation Warning */}
              {employeeProgressionStatus.current_designation_type === 'ISOLATED' && actionType === 'promotion' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  Designation promotion is not applicable because the employee currently occupies an isolated designation.
                </div>
              )}

              {/* Regular Increment Form */}
              {actionType === 'regular_increment' && (
                <form onSubmit={handleRegularIncrementSubmit} className="space-y-4">
                  <h4 className="font-medium text-gray-900">Regular Increment Details</h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Increment Type</label>
                      <select
                        value={regularIncrementForm.increment_type}
                        onChange={(e) => setRegularIncrementForm({ ...regularIncrementForm, increment_type: e.target.value })}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2"
                      >
                        <option value="percentage">Percentage</option>
                        <option value="fixed_amount">Fixed Amount</option>
                      </select>
                    </div>
                    {regularIncrementForm.increment_type === 'percentage' ? (
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Percentage</label>
                        <input
                          type="number"
                          step="0.01"
                          value={regularIncrementForm.increment_percentage}
                          onChange={(e) => setRegularIncrementForm({ ...regularIncrementForm, increment_percentage: e.target.value })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2"
                          required
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          value={regularIncrementForm.increment_amount}
                          onChange={(e) => setRegularIncrementForm({ ...regularIncrementForm, increment_amount: e.target.value })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2"
                          required
                        />
                      </div>
                    )}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Effective Date</label>
                      <input
                        type="date"
                        value={regularIncrementForm.applicable_date}
                        onChange={(e) => setRegularIncrementForm({ ...regularIncrementForm, applicable_date: e.target.value })}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Remarks</label>
                      <textarea
                        value={regularIncrementForm.remarks}
                        onChange={(e) => setRegularIncrementForm({ ...regularIncrementForm, remarks: e.target.value })}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2"
                        rows="2"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-400"
                    disabled={submitting || !employeeProgressionStatus.basic_pay_available}
                  >
                    {submitting ? 'Submitting...' : 'Approve Regular Increment'}
                  </button>
                </form>
              )}

              {/* Promotion Form */}
              {actionType === 'promotion' && (
                <form onSubmit={handlePromotionSubmit} className="space-y-4">
                  <h4 className="font-medium text-gray-900">Promotion Details</h4>
                  
                  {/* Financial Progression Status */}
                  {employeeProgressionStatus.functional_promotion_history.length > 0 && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <p className="font-medium text-blue-900 mb-2">Financial Progression Status</p>
                      {employeeProgressionStatus.functional_promotion_history.map((fp) => (
                        <div key={fp.id} className="text-sm text-blue-800">
                          {fp.milestone_years}-Year Functional Promotion: Granted
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        New Designation <span className="text-red-500">*</span>
                      </label>
                      <SearchableSelect
                        value={promotionForm.to_designation_id}
                        onChange={(val) => setPromotionForm({ ...promotionForm, to_designation_id: val })}
                        options={validProgressionDestinationOptions}
                        placeholder="Select destination designation"
                        required
                        disabled={employeeProgressionStatus.valid_promotion_destinations.length === 0}
                        isClearable
                      />
                      {employeeProgressionStatus.valid_promotion_destinations.length === 0 && (
                        <p className="mt-1 text-xs text-gray-500">No valid promotion destinations available</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={promotionForm.change_basic_pay}
                          onChange={(e) => setPromotionForm({ ...promotionForm, change_basic_pay: e.target.checked })}
                        />
                        Change Basic Pay
                      </label>
                    </div>
                    {promotionForm.change_basic_pay && (
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">New Basic Salary</label>
                        <input
                          type="number"
                          step="0.01"
                          value={promotionForm.new_basic_salary}
                          onChange={(e) => setPromotionForm({ ...promotionForm, new_basic_salary: e.target.value })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2"
                          required
                        />
                      </div>
                    )}
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Effective Date</label>
                      <input
                        type="date"
                        value={promotionForm.effective_date}
                        onChange={(e) => setPromotionForm({ ...promotionForm, effective_date: e.target.value })}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Remarks</label>
                      <textarea
                        value={promotionForm.remarks}
                        onChange={(e) => setPromotionForm({ ...promotionForm, remarks: e.target.value })}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2"
                        rows="2"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-400"
                    disabled={submitting || !employeeProgressionStatus.basic_pay_available || employeeProgressionStatus.valid_promotion_destinations.length === 0}
                  >
                    {submitting ? 'Submitting...' : 'Approve Promotion'}
                  </button>
                </form>
              )}

              {/* Functional Promotion Form */}
              {actionType === 'functional_promotion' && (
                <form onSubmit={handleFunctionalPromotionSubmit} className="space-y-4">
                  <h4 className="font-medium text-gray-900">Functional Promotion Details</h4>
                  
                  {employeeProgressionStatus.functional_promotion_eligibility.length === 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                      Employee is not currently eligible for any functional promotion milestones.
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Eligible Milestone</label>
                        <select
                          value={functionalPromotionForm.milestone_years}
                          onChange={(e) => setFunctionalPromotionForm({ ...functionalPromotionForm, milestone_years: e.target.value })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2"
                          required
                        >
                          <option value="">Select milestone</option>
                          {employeeProgressionStatus.functional_promotion_eligibility.map((milestone) => (
                            <option key={milestone.milestone_years} value={milestone.milestone_years}>
                              {milestone.milestone_years} Years ({milestone.increase_percentage}% increase)
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {functionalPromotionForm.milestone_years && (
                        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                          <p className="text-sm text-green-800">
                            Configured Benefit: {employeeProgressionStatus.functional_promotion_eligibility.find(
                              m => m.milestone_years === Number(functionalPromotionForm.milestone_years)
                            )?.increase_percentage}% increase
                          </p>
                        </div>
                      )}
                      
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Effective Date</label>
                          <input
                            type="date"
                            value={functionalPromotionForm.effective_date}
                            onChange={(e) => setFunctionalPromotionForm({ ...functionalPromotionForm, effective_date: e.target.value })}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2"
                            required
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Remarks</label>
                          <textarea
                            value={functionalPromotionForm.remarks}
                            onChange={(e) => setFunctionalPromotionForm({ ...functionalPromotionForm, remarks: e.target.value })}
                            className="w-full rounded-lg border border-gray-200 px-3 py-2"
                            rows="2"
                          />
                        </div>
                      </div>
                      
                      <button
                        type="submit"
                        className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-400"
                        disabled={submitting || !employeeProgressionStatus.basic_pay_available}
                      >
                        {submitting ? 'Submitting...' : 'Approve Functional Promotion'}
                      </button>
                    </>
                  )}
                </form>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const _old_renderHistory = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Progression History</h3>
      {loadingHistory ? (
        <p className="text-sm text-gray-500">Loading history...</p>
      ) : combinedHistory.length === 0 ? (
        <p className="text-sm text-gray-500">No progression history found.</p>
      ) : (
        <div className="space-y-3">
          {combinedHistory.map((item, index) => (
            <div key={index} className="rounded-lg border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-900">{item.type}</p>
                <span className="text-xs text-gray-400">{item.date}</span>
              </div>
              <p className="mt-1 text-sm text-gray-600">{item.details}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const _old_renderConfiguration = () => (
    <div className="space-y-6">
      {/* Promotion Mapping */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Promotion Mapping</h3>
          <button
            type="button"
            onClick={loadConfiguration}
            className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>
        
        {/* Add New Mapping */}
        <form onSubmit={handleAddMapping} className="mb-6 grid gap-4 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Department</label>
            <select
              value={newMapping.department_id}
              onChange={(e) => {
                setNewMapping({ ...newMapping, department_id: e.target.value, from_designation_id: '', to_designation_id: '' });
              }}
              className="w-full rounded-lg border border-gray-200 px-3 py-2"
              required
            >
              <option value="">Select department</option>
              {departments.map((dept) => (
                <option key={dept.value} value={dept.value}>{dept.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">From Designation</label>
            <select
              value={newMapping.from_designation_id}
              onChange={(e) => setNewMapping({ ...newMapping, from_designation_id: e.target.value, to_designation_id: '' })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2"
              required
              disabled={!newMapping.department_id}
            >
              <option value="">Select from designation</option>
              {filteredFromDesignations.map((desig) => (
                <option key={desig.value} value={desig.value}>{desig.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">To Designation</label>
            <select
              value={newMapping.to_designation_id}
              onChange={(e) => setNewMapping({ ...newMapping, to_designation_id: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2"
              required
              disabled={!newMapping.from_designation_id}
            >
              <option value="">Select to designation</option>
              {filteredToDesignations.map((desig) => (
                <option key={desig.value} value={desig.value}>{desig.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-400"
              disabled={submitting || !newMapping.department_id || !newMapping.from_designation_id || !newMapping.to_designation_id}
            >
              {submitting ? 'Adding...' : 'Add Mapping'}
            </button>
          </div>
        </form>
        
        {/* Existing Mappings */}
        {loadingMappings ? (
          <p className="text-sm text-gray-500">Loading mappings...</p>
        ) : promotionMappings.length === 0 ? (
          <p className="text-sm text-gray-500">No promotion mappings configured.</p>
        ) : (
          <div className="space-y-2">
            {promotionMappings.map((mapping) => (
              <div key={mapping.id} className="flex items-center justify-between rounded-lg border border-gray-100 p-3">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-700">
                    {mapping.department_name || `Dept ${mapping.department_id}`}
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">
                    {mapping.from_designation_title || `Desig ${mapping.from_designation_id}`}
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">
                    {mapping.to_designation_title || `Desig ${mapping.to_designation_id}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggleMapping(mapping.id, mapping.is_active)}
                  className={`rounded px-3 py-1 text-xs font-medium ${
                    mapping.is_active
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {mapping.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Functional Promotion Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Functional Promotion Rules</h3>
        {loadingConfigs ? (
          <p className="text-sm text-gray-500">Loading configurations...</p>
        ) : functionalConfigs.length === 0 ? (
          <p className="text-sm text-gray-500">No functional promotion rules configured.</p>
        ) : (
          <div className="space-y-2">
            {functionalConfigs.map((config) => (
              <div key={config.id} className="flex items-center justify-between rounded-lg border border-gray-100 p-3">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-900">{config.milestone_years} Years</span>
                  <span className="text-sm text-gray-700">{config.increase_percentage}%</span>
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                    config.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {config.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <span className="text-xs text-gray-400">{config.description || ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Promotion & Increment Management</h2>
            <p className="text-sm text-gray-500">Manage regular increments, promotions, and functional promotions.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              loadDashboardStats();
              loadConfiguration();
              loadHistory();
            }}
            className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>

        {message && (
          <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            messageType === 'success' ? 'border-green-200 bg-green-50 text-green-700' :
            messageType === 'error' ? 'border-red-200 bg-red-50 text-red-700' :
            'border-blue-200 bg-blue-50 text-blue-700'
          }`}>
            {message}
          </div>
        )}

        {/* {renderDashboard()} */}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {[
              { id: 'increment', label: 'Increment', icon: CheckCircle, requiredPermission: 'progression:increment' },
              { id: 'promotion', label: 'Promotion', icon: Award, requiredPermission: 'progression:promotion' },
              { id: 'functional_promotion', label: 'Functional Promotion', icon: TrendingUp, requiredPermission: 'progression:functional_promotion' },
            ].filter(tab => hasPermission(tab.requiredPermission)).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'increment' && renderIncrementTab()}
        {activeTab === 'promotion' && renderEmployeeActionTab('promotion')}
        {activeTab === 'functional_promotion' && renderFunctionalPromotionTab()}
      </div>
    </div>
  );
};

export default PromotionIncrementManagement;
